/**
 * Parses a formatted number string that may contain thousand separators and decimal separators.
 * Handles both European (comma as decimal) and US (period as decimal) number formats:
 * - European format: "1.234,56"
 * - Space-separated thousands (French format): "1 234,56"
 * - US format: "1,234.56"
 *
 * @param value - The formatted number string to parse
 * @returns The parsed number as a float
 */
export const parseFormattedNumber = (value: string): number => {
  const cleaned = value.trim().replace(/\s/g, '')

  if (!cleaned) {
    return NaN
  }

  const lastCommaIndex = cleaned.lastIndexOf(',')
  const lastPeriodIndex = cleaned.lastIndexOf('.')

  // No separators - parse directly
  if (lastCommaIndex === -1 && lastPeriodIndex === -1) {
    return parseFloat(cleaned)
  }

  // Both separators present - rightmost is decimal
  if (lastCommaIndex !== -1 && lastPeriodIndex !== -1) {
    if (lastCommaIndex > lastPeriodIndex) {
      // European: "1.234,56" → remove periods, replace comma with period
      return parseFloat(cleaned.replace(/\./g, '').replace(',', '.'))
    }
    // US: "1,234.56" → remove commas
    return parseFloat(cleaned.replace(/,/g, ''))
  }

  // Single separator type
  const separator = lastCommaIndex !== -1 ? ',' : '.'
  return parseSingleSeparator(cleaned, separator)
}

/**
 * Handles ambiguous single separator cases:
 * - Multiple occurrences: likely thousand separators (1,234,567)
 * - Single with 3 digits after and integer before: likely thousand (1,234)
 * - Otherwise: decimal separator (1.23 or 0.999)
 */
const parseSingleSeparator = (str: string, separator: ',' | '.'): number => {
  const parts = str.split(separator)

  // Multiple separators - must be thousand separators with valid grouping
  if (parts.length > 2) {
    const hasValidThousandGroups =
      parts.slice(1).every((part) => part.length === 3 && /^\d+$/.test(part)) && /^\d+$/.test(parts[0])

    return hasValidThousandGroups ? parseFloat(str.replace(/[.,]/g, '')) : NaN
  }

  // Single separator - ambiguous case
  const [intPart, decimalPart] = parts
  const isLikelyThousandSeparator = decimalPart.length === 3 && /^\d+$/.test(decimalPart) && parseInt(intPart, 10) > 0

  if (isLikelyThousandSeparator) {
    return parseFloat(str.replace(separator, ''))
  }

  // Treat as decimal separator
  return parseFloat(separator === ',' ? str.replace(',', '.') : str)
}
